import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import { Link } from 'react-router-dom';
import ConfirmPopups from './ConfirmPopup';

interface InvoiceCredit {
    id: number;
    jobId: number;
    siteAddress: string;
    dashInvoiceNumber: number;
    reckonInvoiceNumber: number;
    customerName: string;
    customerAddress: string;
    creditDescription: string;
    creditAmountFormatted: string;
    jobInsertedBy: string;
}

const InvoiceCreditData = (props: RouteComponentProps<{}>) => {
    const [loading, setLoading] = React.useState(true);
    const [invoiceCredits, setInvoiceCredits] = React.useState<InvoiceCredit[]>([]);
    const [invoiceCreditTotal, setInvoiceCreditTotal] = React.useState(0);
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
    const [invoiceCreditId, setInvoiceCreditId] = React.useState(0);    //used when deleting
    const [saveDisabled, setSaveDisabled] = React.useState(false);

    React.useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckUserPermission?Permission=ViewSendInvoiceCredits&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                getInvoiceCredits(token ?? "");
            } else {
                //redirect to 403 permission denied
                props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const getInvoiceCredits = (token: string) => {

        axios.get('api/Invoicing/GetInvoiceCredits', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setInvoiceCredits(res.data.credits);
            setInvoiceCreditTotal(res.data.creditTotalFormatted);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const complete = (e: any, itemIndex: number) => {
        e.preventDefault();

        if (saveDisabled) {
            return;
        }
        setSaveDisabled(true);

        var creditItem = invoiceCredits[itemIndex];

        completeCredit(creditItem.id);
    }

    const completeCredit = async (creditId: number) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var creditToComplete = {
            creditId: creditId,
            subId: user.sub
        };

        axios.post('api/Invoicing/CompleteInvoiceCredit', creditToComplete, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
                setSaveDisabled(false);
            } else {
                setSaveDisabled(false);
                toast.success("Credit has been completed successfully.");
                getInvoiceCredits(token ?? "");
            } 
        })
        .catch(error => {
            setSaveDisabled(false);
            toast.error(error.message);
        });
    }

    const deleteCredit = (e: any, itemIndex: number) => {
        e.preventDefault();

        var creditItem = invoiceCredits[itemIndex];
        setInvoiceCreditId(creditItem.id);
        //check if they want to delete
        setShowConfirmDelete(true);
    }

    const hideConfirmDelete = () => {
        setInvoiceCreditId(0);
        setShowConfirmDelete(false);
    }

    const deleteCreditItem = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var creditToDelete = {
            creditId: invoiceCreditId,
            subId: user.sub
        };

        axios.post('api/Invoicing/DeleteInvoiceCredit', creditToDelete, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
                setSaveDisabled(false);
                setInvoiceCreditId(0);
                setShowConfirmDelete(false);
            } else {
                setSaveDisabled(false);
                setInvoiceCreditId(0);
                setShowConfirmDelete(false);
                toast.success("Credit has been deleted successfully.");
                getInvoiceCredits(token ?? "");
            }
        })
        .catch(error => {
            setSaveDisabled(false);
            toast.error(error.message);
        });
    }

    const confirmDelete = <ConfirmPopups heading="Delete Credit" text="Are you sure you want to delete this credit?" showConfirmModal={showConfirmDelete} hideConfirmModal={hideConfirmDelete} saveConfirmModal={deleteCreditItem} />

    const renderDetails = (
        <div>
            <div>
                {confirmDelete}
            </div>
            <h2 className="makeitred">An Adjustment Note / Credit needs to be raised for the following jobs</h2>
            <div className="table__margin overflowAuto">
                <table className="table table__large tableColours">
                    <thead>
                        <tr>
                            <th className="hidden"></th>
                            <th>Job #</th>
                            <th>Job Created By</th>
                            <th>Site Address</th>
                            <th>Inv #</th>
                            <th>Reckon Inv #</th>
                            <th>Customer</th>
                            <th>Credit Reason</th>
                            <th>Amount</th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    {invoiceCredits.map((credit, index) =>
                        <tbody key={credit.id}>
                            <tr>
                                <td className="hidden">{credit.id}</td>
                                <td className="table__text--align">
                                    <Link to={'/job/' + credit.jobId} target="_blank">
                                        {credit.jobId}
                                    </Link>
                                </td>
                                <td className="table__text--align">{credit.jobInsertedBy}</td>
                                <td className="table__text--align">{credit.siteAddress}</td>
                                <td className="table__text--align">{credit.dashInvoiceNumber == 0 ? "" : credit.dashInvoiceNumber}</td>
                                <td className="table__text--align">{credit.reckonInvoiceNumber == 0 ? "" : credit.reckonInvoiceNumber}</td>
                                <td className="table__text--align">{credit.customerName}</td>
                                <td className="table__text--align">{credit.creditDescription}</td>
                                <td className="table__text--align">{credit.creditAmountFormatted}</td>
                                <td className="table__text--align">
                                    <button className="btn btn-success" type="button" onClick={(e) => complete(e, index)} disabled={saveDisabled}>Complete</button>
                                </td>
                                <td className="table__text--align">
                                    <div className="delete--tablecell">
                                        <a className="makeitred" href="#" onClick={(e) => deleteCredit(e, index)}>
                                            <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                        </a>
                                    </div>
                                </td>
                            
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
            <div className={invoiceCredits.length > 0 ? "colAlignRight" : "hidden"}>
                <div className="invoicing__total--large">Total {invoiceCreditTotal}</div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default InvoiceCreditData;